import { tokenStore } from '@/utils/tokenStore'
import { ApolloLink, Observable } from 'apollo-link'
import Echo from 'laravel-echo'

require('pusher-js')

const currentEnvMode = process.env.VUE_APP_ENV

const authEndpoint = process.env.VUE_APP_GRAPHQL_SUBSCRIPTION_AUTH || 'http://localhost:8081/graphql/subscriptions/auth'

class PusherLink extends ApolloLink {
  subscriptions: any[]
  echo: Echo<any>;

  constructor() {
    super()
    this.subscriptions = []

    this.echo = new Echo({
      ...{
        wsHost: process.env.VUE_APP_GRAPHQL_SUBSCRIPTION_HOST || 'localhost',
        wsPort: parseInt(process.env.VUE_APP_GRAPHQL_SUBSCRIPTION_PORT || '6001'),
        key: 'any-text',
        useTLS: false,
        forceTLS: false,
        broadcaster: 'pusher',
        disableStats: true,
        enabledTransports: [
          'ws'
        ],
        // Authorize private channels at subscribe time (not module load): the
        // browser sends the HttpOnly auth cookie, the Bearer header covers the
        // moment right after login when only the in-memory token exists yet.
        authorizer: (channel: { name: string }) => ({
          authorize: (
            socketId: string,
            callback: (error: boolean, data: unknown) => void
          ) => {
            const token = tokenStore.get()

            fetch(authEndpoint, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                // Omit Authorization entirely when there is no in-memory token:
                // an empty-but-present header blocks the backend's cookie-to-header
                // promotion, killing cookie-only channel auth.
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              },
              body: JSON.stringify({
                socket_id: socketId,
                channel_name: channel.name
              })
            })
              .then((response) => response.json())
              // Pusher's authorizer expects a boolean error flag in the first arg.
              // eslint-disable-next-line standard/no-callback-literal
              .then((data) => callback(false, data))
              // eslint-disable-next-line standard/no-callback-literal
              .catch((error) => callback(true, error))
          }
        })
      }
    })
  }

  request(operation: any, forward: any): any {
    return new Observable((observer: any) => {
      let subscriptionChannel: string

      // Check the result of the operation
      forward(operation).subscribe({
        next: (data: any) => {
          // If the operation has the subscription extension, it's a subscription
          subscriptionChannel = this._getChannel(data)

          if (subscriptionChannel) {
            if (currentEnvMode === 'local') {
              console.debug(
                  `%c🚀 [GraphQL Subscription] %c'${operation.operationName}' starting ...`, // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                  'color: #FF9800; font-weight: bold',
                  'color: #FF5722; font-weight: bold',
                  '\n',
                  subscriptionChannel,
                  '\n\n'
              )
            }

            // Create the subscription
            this._createSubscription(subscriptionChannel, observer, operation.operationName)
          } else {
            // No subscription found in the response, pipe data through
            observer.next(data)
            observer.complete()
          }
        }
      })

      // Return an object that will unsubscribe _if_ the query was a subscription.
      return {
        closed: false,
        unsubscribe: () => {
          const operationType = Array.isArray(operation.query?.definitions) && operation.query?.definitions.length > 0 ? operation.query?.definitions[0].operation : undefined
          if (operationType === 'subscription' && subscriptionChannel) {
            this._leaveSubscription(subscriptionChannel, observer, operation.operationName)
          }
        }
      }
    })
  }

  _getChannel(data: any) {
    return data.extensions?.lighthouse_subscriptions?.channel || null
  }

  _createSubscription(subscriptionChannel: any, observer: any, operationName: any) {
    const privateChannelName = subscriptionChannel.split('private-').pop()

    if (!this.subscriptions.find(s => s.channel === subscriptionChannel)) {
      this.subscriptions.push({
        channel: subscriptionChannel, observer: observer
      })
    }
    this.echo.private(privateChannelName)
      .listen('.lighthouse-subscription', (payload: any) => {
        if (!payload.more || observer._subscription._state === 'closed') {
          this._leaveSubscription(subscriptionChannel, observer, operationName)
          return
        }
        const result = payload.result
        if (result && result.data) {
          observer.next({
            data: result.data, extensions: result.extensions
          })
        }
      })
      .error((error: any) => {
        console.warn(
          `%c🚀 [GraphQL Subscription] %c'${operationName}' could not start...\n\n`, // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
          'color: #FF9800; font-weight: bold',
          'color: #FF5722; font-weight: bold',
          error
        )

        if (error !== 403) {
          observer.error(error)
        }
      })
      .subscribed(() => {
        if (currentEnvMode === 'local') {
          console.debug(
            `%c🚀 [GraphQL Subscription] %c'${operationName}' started...`, // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
            'color: #FF9800; font-weight: bold',
            'color: #FF5722; font-weight: bold',
            '\n\n\n'
          )
        }

        observer.next({
          data: {},
          context: {
            authenticated: true
          }
        })
      })
  }

  _leaveSubscription(channel: any, observer: any, operationName: any) {
    if (currentEnvMode === 'local') {
      console.log(
        `%c🚀 [GraphQL Subscription] %c'${operationName}' stopped...\n`, // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
        'color: #FF9800; font-weight: bold',
        'color: #FF5722; font-weight: bold'
      )
    }

    const subscription = this.subscriptions.find(s => s.channel === channel)
    this.echo.leave(channel)
    observer.complete()
    this.subscriptions = this.subscriptions.slice(this.subscriptions.indexOf(subscription), 1)
  }
}

export default PusherLink
